<template>
    <Layout :flushWithHeader="true" :noBottomPadding="true" class="c-referrals-container">
        <template v-if="shouldShowErrorContent">
            <ContentfulContentBlock
                :contentRows="contentfulContent.rows"
                :contentTitle="contentfulContent.title"
            />
        </template>
        <slot v-else></slot>
    </Layout>
</template>

<script setup>
import { computed, onBeforeMount, ref, watch } from 'vue';

import { useActions, useMutations, useState } from '~coreModules/core/js/composables/vuexHelpers';
import useAnalyticsPageView from '~coreModules/analytics/js/composables/use-analytics-pageview';

import { SET_404_ERROR } from '~coreModules/core/js/store';

import runtimeConfig from '~config/config';
import { CONTENT_SLUGS } from '~modules/contentful/js/contentful-constants';
import useReferralsMeta from '~routes/referrals/js/composables/use-referrals-meta';
import { PROFILE_MODULE_NAME } from '~modules/profile/js/profile-store';
import { REFERRALS_MODULE_NAME, GET_REFERRAL_CONTENT } from '~routes/referrals/js/referrals-store';

import Layout from '~modules/core/components/layouts/Layout.vue';
import ContentfulContentBlock from '~modules/contentful/components/ContentfulContentBlock.vue';

const props = defineProps({
    errorSlug: {
        type: String,
        default: null,
    },
    beforeInit: {
        type: Function,
        default: null,
    },
    afterInit: {
        type: Function,
        default: null,
    },
});

useAnalyticsPageView();
useReferralsMeta();

const { profileId, email, firstName } = useState(PROFILE_MODULE_NAME, [
    'profileId',
    'email',
    'firstName',
]);
const { contentfulContent } = useState(REFERRALS_MODULE_NAME, [
    'contentfulContent',
]);

const { getReferralContent } = useActions(REFERRALS_MODULE_NAME, {
    getReferralContent: GET_REFERRAL_CONTENT,
});

const { set404 } = useMutations(SET_404_ERROR, {
    set404: SET_404_ERROR,
});

const hasError = ref(false);

const shouldShowErrorContent = computed(() => (!!props.errorSlug || hasError.value) && !!contentfulContent.value);

async function handleGetReferralErrorContent(errorSlug) {
    try {
        await getReferralContent(errorSlug);
    } catch (error) {
        set404({ message: error?.data?.message });
    }
}

watch(() => props.errorSlug, async (newErrorSlug) => {
    if (newErrorSlug) {
        await handleGetReferralErrorContent(newErrorSlug);
    }
});

onBeforeMount(async () => {
    let shouldContinue = true;

    const errorHandler = () => {
        handleGetReferralErrorContent(CONTENT_SLUGS.REFERRALS_GENERIC_ERROR_SLUG);
        hasError.value = true;
        shouldContinue = false;
    };

    if (props.beforeInit) {
        shouldContinue = await props.beforeInit();
    }

    /* eslint-disable */
    if (shouldContinue && !window['friendbuyAPI']) {
        window['friendbuyAPI'] = window['friendbuyAPI'] || [];
        const friendbuyAPI = window['friendbuyAPI'];

        friendbuyAPI.push(['merchant', runtimeConfig.friendbuy.merchantId]);

        if (profileId.value) {
            friendbuyAPI.push(['track', 'customer',
                {
                    email: email.value,
                    id: profileId.value,
                    firstName: firstName.value,
                },
            ]);
        }

        (function(f, r, n, d, b, u, y) {
            while ((u = n.shift())) {
                (b = f.createElement(r)), (y = f.getElementsByTagName(r)[0]);
                if(!y) break;
                b.async = 1;
                b.onerror = errorHandler;
                b.src = u;
                y.parentNode.insertBefore(b, y);
            }
        })(document, 'script', [
            'https://static.fbot.me/friendbuy.js',
            'https://campaign.fbot.me/' + runtimeConfig.friendbuy.merchantId + '/campaigns.js',
        ]);
    }
    if (shouldContinue && props.afterInit) {
        await props.afterInit(friendbuyAPI);
    }
    /* eslint-enable */
});
</script>

<style lang="scss">
    .c-referrals-container {
        iframe {
            display: block !important;
        }
    }
</style>
